import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LogOut, ClipboardList, CreditCard, Crown, Loader2, Settings, Stethoscope, Info, Receipt, Bell, CalendarCheck, Pill, FileText, Eye } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import UpcomingAppointments from "@/components/UpcomingAppointments";
import PatientProgressBar from "@/components/PatientProgressBar";
import TreatmentStatus from "@/components/TreatmentStatus";
import ProgressTracker from "@/components/ProgressTracker";
import ActivityHistory from "@/components/ActivityHistory";
import FollowUpReminder from "@/components/FollowUpReminder";
import MessageInbox from "@/components/MessageInbox";
import AsyncConsultExplainer from "@/components/AsyncConsultExplainer";
import { useAuditLog } from "@/hooks/useAuditLog";
import { useUserRole } from "@/hooks/useUserRole";
import { useToast } from "@/hooks/use-toast";
import { useBrowserNotifications } from "@/hooks/useBrowserNotifications";
import { toast as sonnerToast } from "sonner";
import { MessageSquare } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";

interface Profile {
  first_name: string | null;
  last_name: string | null;
  assigned_provider_id: string | null;
  last_checkup_date: string | null;
}

interface Assessment {
  id: string;
  goal: string | null;
  peptide_interest: string | null;
  status: string;
  triage_level: string | null;
  conditions: string | null;
  allergies: string | null;
  medications: string | null;
  additional_info: string | null;
  created_at: string;
  updated_at: string;
}

interface SubscriptionStatus {
  subscribed: boolean;
  product_id: string | null;
  subscription_end: string | null;
}

const PLAN_MAP: Record<string, { name: string; badge: string }> = {
  "prod_UKAhiaNLwZ5KAl": { name: "Monthly Membership", badge: "Active" },
  "prod_UKAhVJzNA9kEQX": { name: "Annual Membership", badge: "Active" },
  "prod_UKdtrTuPWYw4DL": { name: "Core Membership", badge: "Active" },
  "prod_UKdt0iublEBLXE": { name: "Metabolic Support Add-On", badge: "Active" },
  "prod_UKdu7a6YtFjhMf": { name: "Energy & Cellular Support Add-On", badge: "Active" },
  "prod_UKeEc1n1JvBWvr": { name: "Recovery & Performance Add-On", badge: "Active" },
  "prod_UKeE3hIH14CJmg": { name: "Hormonal Optimization Add-On", badge: "Active" },
};

const friendlyStatus: Record<string, { label: string; description: string; color: string }> = {
  pending: {
    label: "Under Review",
    description: "We're reviewing your health profile. You'll be notified when a provider has reviewed it.",
    color: "bg-yellow-100 text-yellow-800",
  },
  reviewed: {
    label: "Action Needed",
    description: "Your provider has reviewed your profile. Schedule a consultation to discuss your treatment plan.",
    color: "bg-blue-100 text-blue-800",
  },
  approved: {
    label: "Cleared for Treatment",
    description: "Your provider has cleared you for treatment. A prescription will be sent after your consultation.",
    color: "bg-green-100 text-green-800",
  },
  rejected: {
    label: "Needs Attention",
    description: "Your assessment needs additional information. Please contact support for next steps.",
    color: "bg-red-100 text-red-800",
  },
};

const Dashboard = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [subLoading, setSubLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [providerName, setProviderName] = useState<string | null>(null);
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const { logAccess } = useAuditLog();
  const { permission, requestPermission, isSupported } = useBrowserNotifications();
  const { isAdmin, isProvider, isSalesperson, isPharmacy } = useUserRole();
  const messagesSectionRef = useRef<HTMLDivElement>(null);
  const messagesVisibleRef = useRef(false);
  const senderCacheRef = useRef<Record<string, string>>({});

  // Track whether the messages section is scrolled into view
  useEffect(() => {
    const el = messagesSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { messagesVisibleRef.current = entry.isIntersecting; },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Realtime in-app toast for new messages, gated by reactive pref ref
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("dashboard-msg-toast")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        async (payload) => {
          if (!msgNotificationsEnabledRef.current) return;

          const msg = payload.new as { sender_id: string; recipient_id: string; body: string };
          if (msg.recipient_id !== user.id) return;
          if (messagesVisibleRef.current) return;

          // Resolve sender name
          let name = senderCacheRef.current[msg.sender_id];
          if (!name) {
            const { data } = await supabase.from("profiles").select("first_name, last_name").eq("user_id", msg.sender_id).single();
            name = data ? `${data.first_name || ""} ${data.last_name || ""}`.trim() || "Your Care Team" : "Your Care Team";
            senderCacheRef.current[msg.sender_id] = name;
          }

          sonnerToast(`New message from ${name}`, {
            description: msg.body.length > 80 ? msg.body.slice(0, 77) + "…" : msg.body,
            icon: <MessageSquare className="h-4 w-4 text-primary" />,
            action: {
              label: "View",
              onClick: () => messagesSectionRef.current?.scrollIntoView({ behavior: "smooth" }),
            },
          });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  // Track notification preferences reactively via refs
  const msgNotificationsEnabledRef = useRef(true);
  const apptRemindersEnabledRef = useRef(true);
  const rxUpdatesEnabledRef = useRef(true);
  const assessmentUpdatesEnabledRef = useRef(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetchPref = async () => {
      const { data: prefs } = await supabase
        .from("notification_preferences")
        .select("message_notifications, appointment_reminders, prescription_updates, assessment_updates")
        .eq("user_id", user.id)
        .single();
      if (!cancelled && prefs) {
        msgNotificationsEnabledRef.current = prefs.message_notifications !== false;
        apptRemindersEnabledRef.current = prefs.appointment_reminders !== false;
        rxUpdatesEnabledRef.current = prefs.prescription_updates !== false;
        assessmentUpdatesEnabledRef.current = prefs.assessment_updates !== false;
      }
    };
    fetchPref();

    const prefChannel = supabase
      .channel("notif-pref-sync")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notification_preferences", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const row = payload.new as { message_notifications?: boolean; appointment_reminders?: boolean; prescription_updates?: boolean; assessment_updates?: boolean } | undefined;
          if (row) {
            msgNotificationsEnabledRef.current = row.message_notifications !== false;
            apptRemindersEnabledRef.current = row.appointment_reminders !== false;
            rxUpdatesEnabledRef.current = row.prescription_updates !== false;
            assessmentUpdatesEnabledRef.current = row.assessment_updates !== false;
          }
        }
      )
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(prefChannel); };
  }, [user]);

  // Realtime toast for assessment status changes, gated by assessment_updates pref
  useEffect(() => {
    if (!user) return;

    const ASSESSMENT_LABELS: Record<string, string> = {
      reviewed: "Your health assessment has been reviewed 📋",
      approved: "Your assessment has been approved ✅",
      rejected: "Your assessment needs attention ⚠️",
    };

    const channel = supabase
      .channel("dashboard-assessment-toast")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "health_assessments" },
        (payload) => {
          if (!assessmentUpdatesEnabledRef.current) return;

          const assessment = payload.new as { id: string; user_id: string; status: string };
          const old = payload.old as { status?: string };
          if (assessment.user_id !== user.id) return;
          if (assessment.status === old.status) return;

          const label = ASSESSMENT_LABELS[assessment.status] || `Assessment status updated to ${assessment.status}`;

          sonnerToast(label, {
            description: "Check your dashboard for details.",
            icon: <FileText className="h-4 w-4 text-primary" />,
          });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  // Realtime toast for appointment status changes, gated by reactive pref ref
  useEffect(() => {
    if (!user) return;

    const STATUS_LABELS: Record<string, string> = {
      confirmed: "Your appointment has been confirmed ✅",
      cancelled: "Your appointment has been cancelled",
      completed: "Your appointment has been marked as completed",
      rescheduled: "Your appointment has been rescheduled",
      no_show: "You were marked as a no-show for your appointment",
    };

    const channel = supabase
      .channel("dashboard-appt-toast")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "appointments" },
        (payload) => {
          if (!apptRemindersEnabledRef.current) return;

          const appt = payload.new as { id: string; patient_id: string; status: string; start_time: string };
          const old = payload.old as { status?: string };
          if (appt.patient_id !== user.id) return;
          if (appt.status === old.status) return;

          const label = STATUS_LABELS[appt.status] || `Appointment status changed to ${appt.status}`;
          const when = new Date(appt.start_time).toLocaleDateString("en-US", {
            weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
          });

          sonnerToast(label, {
            description: when,
            duration: 8000,
            icon: <CalendarCheck className="h-4 w-4 text-primary" />,
          });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  // Realtime toast for prescription status changes, gated by prescription_updates pref
  useEffect(() => {
    if (!user) return;

    const RX_LABELS: Record<string, string> = {
      sent: "Your prescription has been sent to the pharmacy 📋",
      filled: "Your prescription has been filled ✅",
      shipped: "Your prescription has been shipped 📦",
      delivered: "Your prescription has been delivered 🎉",
    };

    const channel = supabase
      .channel("dashboard-rx-toast")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "prescriptions" },
        (payload) => {
          if (!rxUpdatesEnabledRef.current) return;

          const rx = payload.new as { id: string; patient_id: string; status: string; peptide_name: string };
          const old = payload.old as { status?: string };
          if (rx.patient_id !== user.id) return;
          if (rx.status === old.status) return;

          const label = RX_LABELS[rx.status] || `Prescription status updated to ${rx.status}`;

          sonnerToast(label, {
            description: rx.peptide_name,
            icon: <Pill className="h-4 w-4 text-primary" />,
          });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const goalIncludesWeight = assessments.some(
    (a) => a.goal?.toLowerCase().includes("weight") || a.peptide_interest?.toLowerCase().includes("semaglutide") || a.peptide_interest?.toLowerCase().includes("tirzepatide")
  );

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      const [profileRes, assessmentRes] = await Promise.all([
        supabase.from("profiles").select("first_name, last_name, assigned_provider_id, last_checkup_date").eq("user_id", user.id).single(),
        supabase.from("health_assessments").select("id, goal, peptide_interest, status, triage_level, conditions, allergies, medications, additional_info, created_at, updated_at").eq("user_id", user.id).order("created_at", { ascending: false }),
      ]);
      if (profileRes.data) {
        setProfile(profileRes.data);
        if (profileRes.data.assigned_provider_id) {
          const { data: provProfile } = await supabase
            .from("profiles")
            .select("first_name, last_name")
            .eq("user_id", profileRes.data.assigned_provider_id)
            .single();
          if (provProfile) {
            setProviderName(`Dr. ${provProfile.last_name || provProfile.first_name || "Unknown"}`);
          }
        }
      }
      if (assessmentRes.data) {
        setAssessments(assessmentRes.data);
        logAccess({ action: "view_assessment", resource_type: "health_assessment", metadata: { count: assessmentRes.data.length } });
      }
    };

    const fetchSubscription = async () => {
      setSubLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("check-subscription");
        if (error) throw error;
        setSubscription(data);
      } catch {
        setSubscription({ subscribed: false, product_id: null, subscription_end: null });
      } finally {
        setSubLoading(false);
      }
    };

    fetchData();
    fetchSubscription();
  }, [user]);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Could not open subscription portal.", variant: "destructive" });
    } finally {
      setPortalLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const displayName = profile?.first_name
    ? `${profile.first_name}${profile.last_name ? ` ${profile.last_name}` : ""}`
    : user?.email;

  const currentPlan = subscription?.product_id ? PLAN_MAP[subscription.product_id] : null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Role-based navigation banner */}
      {(isPharmacy || isProvider || isSalesperson || isAdmin) && (
        <div className="bg-primary/10 border-b border-primary/20">
          <div className="container mx-auto px-4 py-2 flex items-center justify-between max-w-4xl">
            <p className="text-sm text-foreground">
              You have a dedicated dashboard for your role.
            </p>
            <div className="flex gap-2">
              {isAdmin && <Link to="/admin" className="text-sm font-medium text-primary hover:underline">Admin Dashboard</Link>}
              {isProvider && <Link to="/provider" className="text-sm font-medium text-primary hover:underline">Provider Dashboard</Link>}
              {isSalesperson && <Link to="/sales" className="text-sm font-medium text-primary hover:underline">Sales Dashboard</Link>}
              {isPharmacy && <Link to="/pharmacy" className="text-sm font-medium text-primary hover:underline">Pharmacy Dashboard</Link>}
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 pt-24 pb-16 max-w-4xl overflow-x-hidden">
        {/* 1. Welcome Header */}
        <div className="flex items-center justify-between mb-8 gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-3xl text-foreground truncate">
              Welcome, {displayName}
            </h1>
            <p className="text-muted-foreground mt-1">Your Elara Health portal</p>
          </div>
          <Button variant="outline" onClick={handleSignOut} className="gap-2 shrink-0">
            <LogOut size={16} /> Sign Out
          </Button>
        </div>

        {/* 2. Progress Bar (Primary Navigation Hub) */}
        <ErrorBoundary label="Progress bar">
          <PatientProgressBar />
        </ErrorBoundary>

        {/* 3. Follow-up Reminder */}
        <ErrorBoundary label="Follow-up reminder">
          <FollowUpReminder />
        </ErrorBoundary>

        {/* Notification opt-in banner */}
        {isSupported && permission === "default" && (
          <Card className="border-primary/20 bg-primary/5 mb-6">
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Bell size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Stay in the loop</p>
                  <p className="text-xs text-muted-foreground">Get notified when your provider sends you a message.</p>
                </div>
              </div>
              <Button size="sm" onClick={requestPermission} className="shrink-0">
                Enable Notifications
              </Button>
            </CardContent>
          </Card>
        )}
        <Card className="border-border bg-card mb-6">
          <CardContent className="p-6">
            {subLoading ? (
              <div className="flex items-center gap-3 text-muted-foreground">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-sm">Checking subscription status…</span>
              </div>
            ) : subscription?.subscribed && currentPlan ? (
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Crown size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground text-sm">{currentPlan.name}</h3>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                        {currentPlan.badge}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Renews {subscription.subscription_end ? new Date(subscription.subscription_end).toLocaleDateString() : "—"}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleManageSubscription} disabled={portalLoading} className="gap-2">
                  {portalLoading ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                  Manage Subscription
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <CreditCard size={20} className="text-muted-foreground" />
                  </div>
                  <div>
                    {assessments.length > 0 ? (
                      <>
                        <h3 className="font-semibold text-foreground text-sm">Choose Your Plan</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">Your assessment is submitted. Pick a plan while your provider reviews it.</p>
                      </>
                    ) : (
                      <>
                        <h3 className="font-semibold text-foreground text-sm">Get Started</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">Complete your health assessment to begin your personalized treatment journey.</p>
                      </>
                    )}
                  </div>
                </div>
                <Button size="sm" onClick={() => navigate(assessments.length > 0 ? "/customize" : "/get-started")} className="gap-2">
                  {assessments.length > 0 ? "View Plans" : "Start Assessment"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Care Team */}
        {(providerName || profile?.last_checkup_date) && (
          <Card className="border-border bg-card mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Stethoscope size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground text-sm">Your Care Team</h3>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1">
                    {providerName && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{providerName}</span>, Assigned Provider
                      </p>
                    )}
                    {profile?.last_checkup_date && (
                      <p className="text-xs text-muted-foreground">
                        Last checkup: <span className="font-medium text-foreground">{new Date(profile.last_checkup_date).toLocaleDateString()}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 4. Upcoming Appointments */}
        <div className="mb-6">
          <ErrorBoundary label="Upcoming appointments">
            <UpcomingAppointments />
          </ErrorBoundary>
        </div>

        {/* 5. Treatment Status (Prescriptions + Orders) */}
        <ErrorBoundary label="Treatment status">
          <TreatmentStatus />
        </ErrorBoundary>

        {/* 6. Messages */}
        <div ref={messagesSectionRef} className="space-y-4">
          <ErrorBoundary label="How consultations work">
            <AsyncConsultExplainer />
          </ErrorBoundary>
          <ErrorBoundary label="Messages">
            <MessageInbox
              recipientId={profile?.assigned_provider_id || undefined}
              recipientName={providerName || "Your Provider"}
            />
          </ErrorBoundary>
        </div>

        {/* 7. Progress Tracker (conditional on weight-loss goal) */}
        <ErrorBoundary label="Progress tracker">
          <ProgressTracker goalIncludesWeight={goalIncludesWeight} />
        </ErrorBoundary>

        {/* 8. Activity History */}
        <ErrorBoundary label="Activity history">
          <ActivityHistory />
        </ErrorBoundary>

        {/* 7. Assessments — latest prominent, older collapsed */}
        <div className="mb-10">
          <h2 className="font-display text-lg text-foreground mb-4">Your Assessments</h2>
          {assessments.length === 0 ? (
            <Card className="border-border bg-card">
              <CardContent className="p-8 text-center">
                <ClipboardList size={40} className="mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No assessments yet.</p>
                <Button className="mt-4" onClick={() => navigate("/get-started")}>Start Your First Assessment</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {/* Latest assessment — prominent card */}
              {(() => {
                const latest = assessments[0];
                const status = friendlyStatus[latest.status] || { label: latest.status, description: "", color: "" };
                return (
                  <Card key={latest.id} className="border-primary/30 bg-primary/5">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Latest</span>
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1 flex-1">
                          <p className="font-medium text-foreground text-sm">{latest.goal || "Health Assessment"}</p>
                          <p className="text-xs text-muted-foreground">
                            {latest.peptide_interest && `${latest.peptide_interest} · `}
                            {latest.updated_at && latest.updated_at !== latest.created_at
                              ? `Updated ${new Date(latest.updated_at).toLocaleDateString()}`
                              : new Date(latest.created_at).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">{status.description}</p>
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap inline-flex items-center gap-1 cursor-help ${status.color}`}>
                                {status.label}
                                <Info size={12} />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-[220px] text-xs">
                              {status.description}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Older assessments — subdued */}
              {assessments.length > 1 && (
                <details className="group">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors py-1">
                    {assessments.length - 1} previous assessment{assessments.length > 2 ? "s" : ""}
                  </summary>
                  <div className="space-y-2 mt-2">
                    {assessments.slice(1).map((a) => {
                      const status = friendlyStatus[a.status] || { label: a.status, description: "", color: "" };
                      return (
                        <Card
                          key={a.id}
                          className="border-border bg-card hover:bg-secondary/40 transition-colors cursor-pointer"
                          onClick={() => setSelectedAssessment(a)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="space-y-0.5 flex-1">
                                <p className="font-medium text-foreground text-xs flex items-center gap-1.5">
                                  {a.goal || "Health Assessment"}
                                  <Eye size={12} className="text-muted-foreground" />
                                </p>
                                <p className="text-[11px] text-muted-foreground">
                                  {new Date(a.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${status.color}`}>
                                {status.label}
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        {/* 8. Quick Links */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-border bg-card hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/settings")}>
            <CardContent className="p-4 flex flex-col items-center text-center gap-2">
              <Settings size={20} className="text-muted-foreground" />
              <p className="text-xs font-medium text-foreground">Settings</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card hover:shadow-md transition-shadow cursor-pointer" onClick={handleManageSubscription}>
            <CardContent className="p-4 flex flex-col items-center text-center gap-2">
              <Receipt size={20} className="text-muted-foreground" />
              <p className="text-xs font-medium text-foreground">Billing</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/get-started")}>
            <CardContent className="p-4 flex flex-col items-center text-center gap-2">
              <ClipboardList size={20} className="text-muted-foreground" />
              <p className="text-xs font-medium text-foreground">New Assessment</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Assessment Detail Drawer */}
      <Drawer open={!!selectedAssessment} onOpenChange={(open) => !open && setSelectedAssessment(null)}>
        <DrawerContent className="max-h-[85vh]">
          {selectedAssessment && (() => {
            const a = selectedAssessment;
            const status = friendlyStatus[a.status] || { label: a.status, description: "", color: "" };
            const detailRows: { label: string; value: string | null }[] = [
              { label: "Goal", value: a.goal },
              { label: "Peptide Interest", value: a.peptide_interest },
              { label: "Conditions", value: a.conditions },
              { label: "Allergies", value: a.allergies },
              { label: "Medications", value: a.medications },
              { label: "Additional Info", value: a.additional_info },
              { label: "Triage Level", value: a.triage_level },
            ];
            return (
              <>
                <DrawerHeader className="pb-2">
                  <DrawerTitle className="font-display text-lg flex items-center gap-2 flex-wrap">
                    {a.goal || "Health Assessment"}
                    <Badge variant="outline" className={`text-[11px] ${status.color}`}>{status.label}</Badge>
                  </DrawerTitle>
                  <p className="text-xs text-muted-foreground">
                    Submitted {new Date(a.created_at).toLocaleDateString()} · Last updated {new Date(a.updated_at).toLocaleDateString()}
                  </p>
                </DrawerHeader>
                <div className="px-4 pb-6 space-y-3 overflow-y-auto">
                  {detailRows.map((row) => (
                    row.value ? (
                      <div key={row.label} className="space-y-0.5">
                        <p className="text-xs font-medium text-muted-foreground">{row.label}</p>
                        <p className="text-sm text-foreground">{row.value}</p>
                      </div>
                    ) : null
                  ))}
                  {detailRows.every((r) => !r.value) && (
                    <p className="text-sm text-muted-foreground text-center py-4">No details recorded for this assessment.</p>
                  )}
                </div>
              </>
            );
          })()}
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default Dashboard;
